/**
 * Admin Service Config API
 *
 * CRUD for per-org service configuration stored in Firestore.
 * All routes live under /admin/config/{service}.
 *
 * Supported services: email | scheduler | gluon_link | sandbox | realtime | media
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

/**
 * Fetch the Firestore config doc for a service.
 * Returns { configured: false } when no doc exists yet.
 * @param {string} service
 * @returns {Promise<Object>}
 */
export const getServiceConfig = async (service) => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_SERVICE_CONFIG(service) });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error getting ${service} config`, { error: error.message });
    throw error;
  }
};

/**
 * Upsert the Firestore config doc for a service.
 * @param {string} service
 * @param {Object} data  Key/value pairs to store (the `configured` flag is stripped server-side).
 * @returns {Promise<Object>}
 */
export const updateServiceConfig = async (service, data) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_SERVICE_CONFIG(service),
      data,
    });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error saving ${service} config`, { error: error.message });
    throw error;
  }
};

/**
 * Trigger GCP Cloud Scheduler deploy for all scheduler jobs.
 * Reads config from Firestore on the backend — call after saving config.
 * @returns {Promise<Object>}  Per-job result map, e.g. { "prompt_scheduler": "ok", "daily-cleanup": "ok" }
 */
export const deployScheduler = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_SCHEDULER_DEPLOY });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", "Error deploying scheduler", { error: error.message });
    throw error;
  }
};

/**
 * Fetch credential status for a service.
 * Returns { configured: false } or { configured: true, last4: "xxxx" }.
 * The raw credential value is never returned by the server.
 * @param {string} service  email | gluon_link | scheduler
 * @returns {Promise<Object>}
 */
export const getServiceCredential = async (service) => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_SERVICE_CREDENTIAL(service) });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error getting ${service} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Seal and store a credential ATOM for a service.
 * @param {string} service
 * @param {Object} data  For email: { credential_key, credential_secret }.
 *                       For gluon_link/scheduler: { api_key }.
 * @returns {Promise<Object>}  { configured: true, last4: "xxxx" }
 */
export const updateServiceCredential = async (service, data) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_SERVICE_CREDENTIAL(service),
      data,
    });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error saving ${service} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Remove the credential ATOM from a service config doc.
 * The service will fall back to Secret Manager credentials on next restart.
 * @param {string} service
 * @returns {Promise<void>}
 */
export const resetServiceCredential = async (service) => {
  try {
    await api({ method: "DELETE", url: ENDPOINTS.ADMIN_SERVICE_CREDENTIAL(service) });
  } catch (error) {
    logger.error("adminConfig", `Error revoking ${service} credential`, { error: error.message });
    throw error;
  }
};

// =============================================================================
// Auth-specific named credential atoms (idp | jwt)
// The auth service has two GCCS atoms — these use dedicated sub-paths:
//   /admin/config/auth/credential/idp  (IDP API key)
//   /admin/config/auth/credential/jwt  (JWT signing key)
// =============================================================================

/**
 * Fetch status of a named auth credential atom.
 * @param {"idp"|"jwt"} credentialName
 * @returns {Promise<{configured: boolean, last4?: string}>}
 */
export const getAuthCredential = async (credentialName) => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_AUTH_CREDENTIAL(credentialName) });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error getting auth/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Seal and store a named auth credential atom.
 * @param {"idp"|"jwt"} credentialName
 * @param {Object} data  For "idp": { api_key }. For "jwt": { signing_key }.
 * @returns {Promise<{configured: boolean, last4: string}>}
 */
export const updateAuthCredential = async (credentialName, data) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_AUTH_CREDENTIAL(credentialName),
      data,
    });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error saving auth/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Remove a named auth credential atom from the config/auth doc.
 * @param {"idp"|"jwt"} credentialName
 * @returns {Promise<void>}
 */
export const resetAuthCredential = async (credentialName) => {
  try {
    await api({ method: "DELETE", url: ENDPOINTS.ADMIN_AUTH_CREDENTIAL(credentialName) });
  } catch (error) {
    logger.error("adminConfig", `Error revoking auth/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Seal the plaintext bootstrap field written by setup_firestore --auth into a GCCS atom.
 * The backend reads the value from Firestore, seals it, and deletes the plaintext field.
 * @param {"idp"|"jwt"} credentialName
 * @returns {Promise<{configured: boolean, last4: string}>}
 */
export const sealAuthCredential = async (credentialName) => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_AUTH_CREDENTIAL_SEAL(credentialName) });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error sealing auth/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

export const activateJwtRotation = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_JWT_ROTATION_ACTIVATE });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", "Error activating JWT rotation", { error: error.message });
    throw error;
  }
};

export const completeJwtRotation = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_JWT_ROTATION_COMPLETE });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", "Error completing JWT rotation rollout", { error: error.message });
    throw error;
  }
};

export const completeCancelledJwtRotation = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_JWT_ROTATION_CANCEL_COMPLETE });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", "Error completing JWT cancellation rollout", { error: error.message });
    throw error;
  }
};

// =============================================================================
// Gluon Link named credential atoms (keypair)
// The keypair atom uses a dedicated sub-path:
//   /admin/config/gluon_link/credential/keypair  (RS256 private key PEM)
// =============================================================================

/**
 * Fetch status of a named gluon_link credential atom.
 * @param {"keypair"} credentialName
 * @returns {Promise<{configured: boolean, last4?: string}>}
 */
export const getGluonCredential = async (credentialName) => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_GLUON_CREDENTIAL(credentialName) });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error getting gluon_link/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Seal and store a named gluon_link credential atom.
 * @param {"keypair"} credentialName
 * @param {Object} data  For "keypair": { private_key_pem: "...", public_key: "..." }.
 * @returns {Promise<{configured: boolean, last4: string}>}
 */
export const updateGluonCredential = async (credentialName, data) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_GLUON_CREDENTIAL(credentialName),
      data,
    });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", `Error saving gluon_link/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Remove the private-key atom, legacy private key, and public key together.
 * @param {"keypair"} credentialName
 * @returns {Promise<void>}
 */
export const resetGluonCredential = async (credentialName) => {
  try {
    await api({ method: "DELETE", url: ENDPOINTS.ADMIN_GLUON_CREDENTIAL(credentialName) });
  } catch (error) {
    logger.error("adminConfig", `Error revoking gluon_link/${credentialName} credential`, { error: error.message });
    throw error;
  }
};

/**
 * Generate a new RS256 keypair server-side and return both keys to the frontend.
 * Nothing is written to Firestore — the user saves via the normal Save buttons.
 * Navigating away discards the generated keys; the existing keys remain active.
 * @returns {Promise<{private_key: string, public_key: string}>}
 */
export const rotateGluonKeypair = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_GLUON_KEYPAIR_ROTATE });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", "Error generating gluon_link keypair", { error: error.message });
    throw error;
  }
};

/**
 * Seal the plaintext private_key written by setup_firestore --gluon-link into a GCCS atom.
 * The backend reads the plaintext from Firestore, seals it, writes the atom, and deletes
 * the plaintext field — no credential value is sent from the browser.
 * @returns {Promise<{configured: boolean, last4: string}>}
 */
export const sealGluonKeypair = async () => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_GLUON_KEYPAIR_SEAL });
    return response.data;
  } catch (error) {
    logger.error("adminConfig", "Error sealing gluon_link keypair", { error: error.message });
    throw error;
  }
};
